/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Linking, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing';
import { SoftPressable } from '../../shared/ui/SoftPressable';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors'
import { PaperSheet } from '../../shared/ticket/PaperSheet';
import { dismissAnnouncement } from './announcementUiStorage';
import type { AppAnnouncement } from './fetchAppAnnouncements';
import { partitionAnnouncementsByKind } from './partitionAnnouncements';

/** Inbox visible rows before “Ver más”; keeps Home calm with many seeds. */
const INBOX_PREVIEW = 5;

type Props = {
  items: AppAnnouncement[];
  userId: string;
  onDismissed: (id: string) => void;
};

function isHttpUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim());
}

function tagLabel(item: AppAnnouncement): string {
  if (item.kind === 'product') return 'Novedad';
  return item.severity === 'warn' ? 'Importante' : 'Aviso';
}

function InboxRow({
  item,
  onDismiss,
  onCta,
}: {
  item: AppAnnouncement;
  onDismiss: (id: string) => void;
  onCta: (url: string) => void;
}) {
  const warn = item.severity === 'warn' && item.kind !== 'product';
  const showCta = Boolean(item.cta_label && item.cta_url && isHttpUrl(item.cta_url));
  return (
    <View
      style={styles.row}
      testID={`home.announcement.${item.kind}.${item.id}`}
    >
      <View style={[styles.tag, warn ? styles.tagWarn : styles.tagInfo]}>
        <Text style={[styles.tagText, warn ? styles.tagTextWarn : styles.tagTextInfo]}>
          {tagLabel(item)}
        </Text>
      </View>
      <View style={styles.rowBody}>
        <Text style={styles.title} numberOfLines={2}>
          {item.title}
        </Text>
        <Text style={styles.body} numberOfLines={3}>
          {item.body}
        </Text>
        {showCta ? (
          <SoftPressable
            style={styles.cta}
            onPress={() => onCta(item.cta_url!)}
            accessibilityRole="link"
            accessibilityLabel={item.cta_label!}
            hoverLift={false}
          >
            <Text style={styles.ctaText}>{item.cta_label} →</Text>
          </SoftPressable>
        ) : null}
      </View>
      <SoftPressable
        style={styles.dismiss}
        onPress={() => onDismiss(item.id)}
        accessibilityRole="button"
        accessibilityLabel={item.kind === 'product' ? 'Cerrar novedad' : 'Cerrar aviso'}
        hitSlop={8}
        hoverLift={false}
      >
        <Ionicons name="close" size={18} color={ticketColors.ink3} />
      </SoftPressable>
    </View>
  );
}

export function HomeAnnouncements({ items, userId, onDismissed }: Props) {
  const [showAll, setShowAll] = useState(false);

  const onDismiss = useCallback(
    async (id: string) => {
      await dismissAnnouncement(userId, id);
      onDismissed(id);
    },
    [onDismissed, userId],
  );

  const onCta = useCallback((url: string) => {
    if (!isHttpUrl(url)) return;
    void Linking.openURL(url.trim());
  }, []);

  const inboxItems = useMemo(() => {
    const { ops, product } = partitionAnnouncementsByKind(items);
    return [...ops, ...product];
  }, [items]);

  const visible = showAll ? inboxItems : inboxItems.slice(0, INBOX_PREVIEW);
  const hiddenCount = Math.max(0, inboxItems.length - visible.length);

  if (items.length === 0) return null;

  return (
    <View style={styles.section} testID="home.announcements">
      <PaperSheet testID="home.announcements.inbox">
        {visible.map((item) => (
          <InboxRow
            key={item.id}
            item={item}
            onDismiss={(id) => void onDismiss(id)}
            onCta={onCta}
          />
        ))}
        {hiddenCount > 0 ? (
          <SoftPressable
            style={styles.moreRow}
            onPress={() => setShowAll(true)}
            accessibilityRole="button"
            accessibilityLabel={`Ver ${hiddenCount} más`}
            testID="home.announcements.more"
            hoverLift={false}
          >
            <Text style={styles.moreText}>Ver {hiddenCount} más</Text>
          </SoftPressable>
        ) : null}
      </PaperSheet>
    </View>
  );
}

const styles = StyleSheet.create({
  section: { gap: 8 },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    paddingVertical: 12,
    paddingLeft: spacing.md,
    paddingRight: 36,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  tag: {
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 6,
    marginTop: 2,
    flexShrink: 0,
  },
  tagWarn: { backgroundColor: '#FEF3C7' },
  tagInfo: { backgroundColor: '#E8F7F4' },
  tagText: {
    fontSize: 9,
    fontWeight: '800',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    fontFamily: ticketFonts.sans,
  },
  tagTextWarn: { color: '#92400E' },
  tagTextInfo: { color: '#115E59' },
  rowBody: { flex: 1, minWidth: 0, gap: 2 },
  dismiss: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
  },
  title: {
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 18,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  body: {
    fontSize: 12,
    lineHeight: 17,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  cta: { marginTop: 6, alignSelf: 'flex-start' },
  ctaText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.gold,
    fontFamily: ticketFonts.sans,
  },
  moreRow: {
    paddingVertical: 12,
    paddingHorizontal: spacing.md,
    alignItems: 'center',
  },
  moreText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#115E59',
    fontFamily: ticketFonts.sans,
  },
});
